// Bridge: parsed `Comparison` → `ResolvedComparison` for the producer.
//
// The parser leaves column operands type-less (it has no schema in scope); the
// collector one layer up supplies a `resolveColumnType` callback that maps a
// column reference to its coarse `OperandLiteralSqlType` from the snapshot.
// Literals already carry `sqlType`; expressions carry `resultSqlType` (Item 2).
// This module is pure and keeps the producer fed by a stable, schema-free shape.

import type {
  Comparison,
  OperandLiteralSqlType,
  OperandType,
} from '@/features/sql-resolver/types';

import type { ResolvedComparison, ResolvedOperand } from './types';

/** Maps a column operand's identifier to its coarse SQL type. Returns
 *  `'unknown'` when the column can't be confidently resolved (not in scope,
 *  ambiguous across in-scope tables, or non-categorisable declared type). */
export type ResolveColumnType = (operand: Extract<OperandType, { kind: 'column' }>) => OperandLiteralSqlType;

export function resolveComparisonTypes(
  comparison: Comparison,
  resolveColumnType: ResolveColumnType,
): ResolvedComparison {
  const resolved: ResolvedComparison = {
    startOffset: comparison.range.startOffset,
    endOffset: comparison.range.endOffset,
    operator: comparison.operator,
    lhs: resolveOperand(comparison.lhs, resolveColumnType),
    ...(comparison.rhs ? { rhs: resolveOperand(comparison.rhs, resolveColumnType) } : {}),
  };
  return resolved;
}

function resolveOperand(
  operand: OperandType,
  resolveColumnType: ResolveColumnType,
): ResolvedOperand {
  if (operand.kind === 'column') {
    return {
      kind: 'column',
      sqlType: resolveColumnType(operand),
      name: operand.ref.qualified.name,
    };
  }
  if (operand.kind === 'literal') {
    return { kind: 'literal', sqlType: operand.sqlType, text: operand.text };
  }
  return {
    kind: 'expression',
    sqlType: operand.resultSqlType ?? 'unknown',
    text: operand.text,
  };
}
